import { useState } from "react";
import axiosInstance from "../../api/axios";
import { useDispatch } from "react-redux/es/hooks/useDispatch";
import { setUpdateAvatar } from "../../store/api/authSlice";
import { RECAPTCHA_SITE_KEY } from "../helpers";

interface UseUploadPhotoReturn {
  uploadPhoto: (file: File) => Promise<{ success: true; data: string } | { success: false; error: string } | undefined>;
  isLoading: boolean;
  error: string | null;
}

type UploadPhotoResult = Awaited<ReturnType<UseUploadPhotoReturn["uploadPhoto"]>>;

export const useUploadPhoto = (): UseUploadPhotoReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();

  const uploadPhoto: UseUploadPhotoReturn["uploadPhoto"] = async (file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        return;
      }

      return await new Promise<UploadPhotoResult>((resolve) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: "submit" });
            console.log("Получен reCAPTCHA token:", token);

            const formData = new FormData();
            formData.append("picture", file);
            formData.append("g-recaptcha-response", token);

            const response = await axiosInstance.post("user/picture/", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });

            dispatch(setUpdateAvatar(response.data.picture));
            if (!response.status) {
              throw new Error(`Ошибка загрузки: ${response.statusText}`);
            }

            console.log("Успешно загружено:", response);

            resolve({ success: true, data: response.data.picture });
          } catch (err: any) {
            console.error("Ошибка при загрузке фото:", err);
            const message = err.message || "Ошибка загрузки";
            setError(message);
            resolve({ success: false, error: message });
          }
        });
      });
    } catch (err: any) {
      console.error("Ошибка при загрузке фото:", err);
      const message = err.message || "Ошибка загрузки";
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    uploadPhoto,
    isLoading,
    error,
  };
};
